import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { CollectionScheduledEmail } from '../model';
import { relativeTimeUntil, scheduleStatusChip, toneChipProps } from '../utils';

interface Props {
  email: CollectionScheduledEmail | null;
  onClose: () => void;
  onSendNow: (email: CollectionScheduledEmail) => void;
  onResumeAutomatic: (email: CollectionScheduledEmail) => void;
}

// email.body is HTML generated server-side from admin-authored templates — not user-supplied freeform content.
// sandbox="" on the iframe provides defense-in-depth. If user-supplied content is ever introduced, add DOMPurify.
const buildPreviewHtml = (body: string): string => `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <style>
      body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4; }
    </style>
  </head>
  <body>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4;">
      <tr>
        <td align="center" style="padding:24px 16px;">
          <table role="presentation" width="600" cellpadding="0" cellspacing="0"
                 style="max-width:600px;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
            <tr>
              <td style="padding:0 40px;">
                <hr style="border:none;border-top:2px solid #96052F;margin:0;" />
              </td>
            </tr>
            <tr>
              <td style="padding:32px 40px;color:#333;font-size:15px;line-height:1.7;font-family:Arial,Helvetica,sans-serif;">
                ${body}
              </td>
            </tr>
            <tr>
              <td style="background:#96052F;padding:20px 40px;text-align:center;">
                <p style="margin:0;color:#fff;font-size:13px;font-family:Arial,Helvetica,sans-serif;">
                  TUDI Collections &bull; <a href="mailto:ar@tudi.com" style="color:#fff;text-decoration:underline;">ar@tudi.com</a>
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>
`;

const sendButtonContent = (email: CollectionScheduledEmail): { label: string; tooltip?: string } => {
  if (email.schedule_status === 'manual_locked') {
    const remaining = relativeTimeUntil(email.manual_send_locked_until);
    return { label: `Available in ${remaining}`, tooltip: 'Manual send recently used.' };
  }
  if (email.schedule_status === 'no_recipients') {
    return { label: 'No recipients', tooltip: 'No email addresses for this account' };
  }
  return { label: 'Send Now' };
};

const ScheduledEmailModal: React.FC<Props> = ({ email, onClose, onSendNow, onResumeAutomatic }) => (
  <Dialog open={!!email} onClose={onClose} maxWidth="md" fullWidth>
    {email && (
      <>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{email.company}</Typography>
            <Chip {...toneChipProps(email.tone)} size="small" />
            {(() => {
              const status = scheduleStatusChip(email);
              return (
                <Tooltip title={status.tooltip} placement="top" arrow>
                  <Chip label={status.label} color={status.color} size="small" variant="outlined" />
                </Tooltip>
              );
            })()}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Subject:</strong> {email.subject}
            </Typography>
            {email.next_send_at && (
              <Typography variant="body2" color="textSecondary">
                <strong>Next automatic send:</strong> {email.next_send_at}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Recipients:</strong>
              </Typography>
              {email.to_emails && email.to_emails.length > 0 ? (
                email.to_emails.map((addr) => <Chip key={addr} label={addr} size="small" />)
              ) : (
                <Chip label="No email addresses" size="small" color="error" variant="outlined" />
              )}
            </Box>
            {email.attachment_metadata && email.attachment_metadata.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Attachments:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {email.attachment_metadata.map((a) => (
                    <Chip key={a.edoc_invoice_import_id} label={a.filename} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          {/* sandbox="" is intentionally maximally restrictive — this is a read-only preview of the email body.
              Links (including mailto:) will not activate. If clickable links are needed in future, add allow-popups. */}
          <iframe
            srcDoc={buildPreviewHtml(email.body)}
            sandbox=""
            title="Email preview"
            style={{ width: '100%', height: 400, border: '1px solid #e0e0e0', borderRadius: 4 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Close</Button>
          {(email.schedule_status === 'auto_skipped' || email.schedule_status === 'auto_paused') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() => onResumeAutomatic(email)}
            >
              Resume automatic
            </Button>
          )}
          {(() => {
            const { label, tooltip } = sendButtonContent(email);
            const sendBtn = (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => onSendNow(email)}
                disabled={!email.can_send_manually}
              >
                {label}
              </Button>
            );
            return tooltip ? (
              <Tooltip title={tooltip} placement="top" arrow>
                <span>{sendBtn}</span>
              </Tooltip>
            ) : (
              sendBtn
            );
          })()}
        </DialogActions>
      </>
    )}
  </Dialog>
);

export default ScheduledEmailModal;
